// The hero's live scene: the poster shows first; on capable devices the 3D scene (the Halloween pack's own bakes)
// loads after first paint, in the sandboxed preview frame, and fades in over it. Reduced motion, Save-Data and
// low-power phones keep the poster.

import { startScenePreview } from "../../preview-frame.ts";

export interface HeroGate {
  reducedMotion: boolean;
  saveData: boolean;
  /** a phone-sized touch screen */
  smallTouch: boolean;
  /** navigator.deviceMemory (GB), when the browser says */
  memory?: number;
  cores?: number;
}

/** Whether to run the 3D scene, or keep the poster. */
export function wantsLiveHero(g: HeroGate): boolean {
  if (g.reducedMotion || g.saveData) return false;
  if (g.memory !== undefined && g.memory < 4) return false;
  if (g.cores !== undefined && g.cores < 4) return false;
  // phones get the poster unless they're clearly strong (8 GB and 8 cores)
  if (g.smallTouch && !((g.memory ?? 0) >= 8 && (g.cores ?? 0) >= 8)) return false;
  return true;
}

/** Bloom and four lights on strong machines; one light and no bloom otherwise. */
export function qualityFor(g: HeroGate): "high" | "low" {
  return !g.smallTouch && (g.memory ?? 8) >= 8 && (g.cores ?? 8) >= 8 ? "high" : "low";
}

export function gateFromBrowser(): HeroGate {
  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } };
  const mq = (q: string) => !!window.matchMedia?.(q).matches;
  return {
    reducedMotion: mq("(prefers-reduced-motion: reduce)"),
    saveData: !!nav.connection?.saveData,
    smallTouch: mq("(pointer: coarse)") && mq("(max-width: 820px)"),
    memory: nav.deviceMemory,
    cores: nav.hardwareConcurrency || undefined,
  };
}

/**
 * Starts the live scene behind the copy when the device can take it. The scene runs in the sandboxed preview frame
 * (src/frame/scenes/open-haunt.ts; three.js never loads in the page itself) and fades in over the poster once it has
 * drawn. `?hero=poster` / `?hero=3d` force either, for the screenshot checks.
 */
export function mountHero(stage: HTMLElement): void {
  const force = new URLSearchParams(location.search).get("hero");
  if (force === "poster") return;
  const gate = gateFromBrowser();
  if (force !== "3d" && !wantsLiveHero(gate)) return;
  const go = () => {
    if (!stage.isConnected) return;
    startScenePreview(stage, "open-haunt", qualityFor(gate), () => {
      stage.classList.add("live");
      mark("zone:hero-live");
    });
  };
  // after the poster and the page's first paint
  if (document.readyState === "complete") requestAnimationFrame(() => setTimeout(go, 0));
  else window.addEventListener("load", go, { once: true });
}

/** A budget mark for the zone framework's checks (zone:first-content, zone:hero). */
export function mark(name: string): void {
  window.performance?.mark(name);
}
